import { CURRENCY } from '../constants';

// Get user's locale from browser
export const getUserLocale = (): string => {
  // Try to get locale from browser
  const browserLocale = navigator.language || navigator.languages?.[0];
  
  // Extract language code (e.g., 'en-US' -> 'en-US', 'tr' -> 'tr-TR')
  if (browserLocale) {
    // If it's just language code (e.g., 'tr'), add country code
    if (browserLocale.length === 2) {
      const languageMap: Record<string, string> = {
        'tr': 'tr-TR',
        'en': 'en-US', 
        'de': 'de-DE',
        'fr': 'fr-FR',
        'es': 'es-ES',
        'it': 'it-IT',
        'ru': 'ru-RU',
        'ar': 'ar-SA',
        'zh': 'zh-CN',
        'ja': 'ja-JP',
        'ko': 'ko-KR'
      };
      return languageMap[browserLocale] || `${browserLocale}-${browserLocale.toUpperCase()}`;
    }
    return browserLocale;
  }
  
  // Fallback to Turkish if no locale detected
  return 'tr-TR';
};

// Number formatting utilities
export const formatPrice = (value: number): string => {
  return new Intl.NumberFormat(CURRENCY.LOCALE, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  }).format(value);
};

export const formatAmount = (value: number): string => {
  return new Intl.NumberFormat(CURRENCY.LOCALE, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  }).format(value);
};

export const formatQuantity = (value: number): string => {
  return new Intl.NumberFormat(CURRENCY.LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Döviz kuru: 2 ondalık, nokta ayraçlı → "45.54"
export const formatExchangeRate = (value: number): string => {
  return value.toFixed(2);
};

// Enhanced formatting functions
export const formatCurrency =(value: number, currency = CURRENCY.SYMBOL): string => {
  return `${currency}${formatAmount(value)}`;
};

export const formatPercentage = (value: number, decimals = 1): string => {
  return `${new Intl.NumberFormat(CURRENCY.LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value)}%`;
};


export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, '');
  
  // Format Turkish phone numbers
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
  }
  
  if (cleaned.length === 11 && cleaned.startsWith('0')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9)}`;
  }
  
  return phone;
};

export const formatTaxNumber = (taxNumber: string): string => {
  const cleaned = taxNumber.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
  }
  return taxNumber;
};

// Validation helpers
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPhoneNumber = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('0'));
};

export const isValidTaxNumber = (taxNumber: string): boolean => {
  const cleaned = taxNumber.replace(/\D/g, '');
  return cleaned.length === 10;
};

// String utilities
export const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

export const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[çğıöşü]/g, (match) => {
      const map: Record<string, string> = {
        'ç': 'c', 'ğ': 'g', 'ı': 'i', 'ö': 'o', 'ş': 's', 'ü': 'u'
      };
      return map[match] || match;
    })
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

// Array utilities
export const chunk = <T>(array: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

export const unique = <T>(array: T[]): T[] => {
  return [...new Set(array)];
};

export const sortBy = <T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

// Object utilities
export const pick = <T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[]
): Pick<T, K> => {
  const result = {} as Pick<T, K>;
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
};

export const omit = <T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[]
): Omit<T, K> => {
  const result = { ...obj };
  keys.forEach(key => {
    delete result[key];
  });
  return result;
};

// Date utilities
export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

export const isToday = (date: Date): boolean => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

export const isYesterday = (date: Date): boolean => {
  const yesterday = addDays(new Date(), -1);
  return date.toDateString() === yesterday.toDateString();
};

// Math utilities
export const roundToDecimals = (value: number, decimals: number): number => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

export const calculatePercentage = (part: number, total: number): number => {
  if (total === 0) return 0;
  return (part / total) * 100;
};

export const calculateVAT = (amount: number, rate: number): number => {
  return amount * rate;
};

export const calculateTotalWithVAT = (amount: number, rate: number): number => {
  return amount + calculateVAT(amount, rate);
};
